import {
  forwardRef,
  LegacyRef,
  MutableRefObject,
  useEffect,
  useState,
} from 'react';
import { QRCode as BaseQRCode } from 'react-qrcode-logo';

import { tw } from '../../utils';

type Props = {
  value: string;
  imageUrl?: string;
  className?: string;
  onRender?: () => void;
};

const cropToCircle = (imageUrl: string) =>
  new Promise<string | undefined>((resolve) => {
    const img = new Image();

    img.onload = () => {
      const size = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;

      canvas.width = size;
      canvas.height = size;

      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      const xOffset = (img.width - size) / 2;
      const yOffset = (img.height - size) / 2;
      ctx.drawImage(img, xOffset, yOffset, size, size, 0, 0, size, size);

      resolve(canvas.toDataURL());
    };

    img.onerror = () => resolve(undefined);

    const url = new URL(imageUrl);
    if (!url.searchParams.has('w')) {
      url.searchParams.set('w', '3840');
    }

    if (!url.searchParams.has('q')) {
      url.searchParams.set('q', '75');
    }

    const parts = url.toString().split('?');

    img.src = `/_next/image?url=${encodeURIComponent(parts[0]!)}&${parts[1]}`;
  });

export type QRCodeInstance = Omit<BaseQRCode, 'canvasRef'> & {
  canvasRef: MutableRefObject<HTMLCanvasElement>;
};

export const QRCode = forwardRef<QRCodeInstance, Props>(
  ({ value, imageUrl, className, onRender }, ref) => {
    const [logo, setLogo] = useState<string | undefined | null>(undefined);

    useEffect(() => {
      if (!imageUrl) {
        onRender?.();
        return;
      }
      cropToCircle(imageUrl).then((data) => {
        setLogo(data || null);
        if (data) return;
        onRender?.();
      });
    }, [imageUrl, onRender]);

    if (logo === undefined) return <></>;

    return (
      <div className={tw('flex overflow-hidden rounded-2xl', className)}>
        <BaseQRCode
          ref={ref as LegacyRef<BaseQRCode>}
          value={value}
          // enableCORS
          logoImage={logo || undefined}
          logoOnLoad={() => onRender?.()}
          logoHeight={64}
          logoWidth={64}
          ecLevel='M'
          size={256}
          qrStyle='dots'
          logoPadding={3}
          logoPaddingStyle='circle'
          fgColor='#80b0ff'
          bgColor='#ffffff'
          eyeRadius={{
            inner: 15,
            outer: 15,
          }}
          eyeColor={{
            inner: '#7fffc5',
            outer: '#7fffc5',
          }}
        />
      </div>
    );
  },
);

QRCode.displayName = 'QRCode';
